import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, MapPin, ChevronDown, Package, Folder, Store, Briefcase, Sparkles } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { api, inr } from '../../lib/api';

const DEFAULT_CITIES_FALLBACK = ['Hyderabad', 'Warangal', 'Nizamabad'];

const HeaderSearch = ({ trending = [], cities = [], placeholder = 'Search products...', searchLabel = 'Search' }) => {
  const navigate = useNavigate();
  const [city, setCity] = useState('All Telangana');
  const [query, setQuery] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [suggestions, setSuggestions] = useState(null);
  const [suggestLoading, setSuggestLoading] = useState(false);
  const debounceRef = useRef(null);

  useEffect(() => {
    const trimmed = query.trim();
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      if (trimmed.length < 2) {
        setSuggestions(null);
        return;
      }
      setSuggestLoading(true);
      api.searchSuggest(trimmed)
        .then((d) => setSuggestions(d))
        .catch(() => setSuggestions(null))
        .finally(() => setSuggestLoading(false));
    }, 200);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [query]);

  const onSearch = (e) => {
    e?.preventDefault();
    if (query.trim()) {
      navigate(`/search?q=${encodeURIComponent(query)}&city=${encodeURIComponent(city)}`);
      setShowSuggestions(false);
    }
  };

  const goToProduct = (p) => {
    navigate(`/product/${p.slug || p.id}`);
    setShowSuggestions(false);
    setQuery('');
  };
  const goToCategory = (c) => {
    navigate(`/category/${c.slug || c.id}`);
    setShowSuggestions(false);
    setQuery('');
  };
  const goToSubCategory = (s) => {
    navigate(`/sub-category/${s.slug || s.id}`);
    setShowSuggestions(false);
    setQuery('');
  };
  const goToVendor = (v) => {
    navigate(`/vendor/${v.slug || v.id}`);
    setShowSuggestions(false);
    setQuery('');
  };
  const goToService = (s) => {
    // Service detail pages don't exist yet; open the providing vendor's profile.
    if (s.supplier_id) navigate(`/supplier/${s.supplier_id}`);
    else navigate(`/directory`);
    setShowSuggestions(false);
    setQuery('');
  };

  const hasSuggestions =
    suggestions && (
      suggestions.products?.length ||
      suggestions.vendors?.length ||
      suggestions.services?.length ||
      suggestions.categories?.length ||
      suggestions.sub_categories?.length
    );

  return (
    <form onSubmit={onSearch} className="order-3 lg:order-2 w-full lg:w-auto lg:flex-1 lg:max-w-3xl flex items-stretch border-2 border-[#ee5e23] rounded-md lg:ml-4 relative h-11 lg:h-auto">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button type="button" className="hidden sm:flex items-center gap-1 px-3 bg-white border-r border-gray-200 text-sm text-gray-700 hover:bg-gray-50 transition-colors sm:min-w-[140px]" data-testid="header-city-dropdown">
            <MapPin className="w-4 h-4 text-[#ee5e23]" />
            <span className="truncate flex-1 text-left">{city}</span>
            <ChevronDown className="w-4 h-4 text-gray-500" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent className="max-h-72 overflow-y-auto w-56">
          {['All Telangana', ...(cities.length ? cities.map((c) => c.name) : DEFAULT_CITIES_FALLBACK)].map((c) => (
            <DropdownMenuItem key={c} onClick={() => setCity(c)} className="cursor-pointer">{c}</DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
      <input
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        onFocus={() => setShowSuggestions(true)}
        onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
        type="text"
        placeholder={placeholder}
        className="flex-1 min-w-0 px-3 py-2 text-sm focus:outline-none bg-white dark:bg-[#111827] text-gray-900 dark:text-gray-100"
        data-testid="header-search-input"
      />
      <button type="submit" className="bg-[#ee5e23] hover:bg-[#d24e15] transition-colors text-white px-4 sm:px-5 flex items-center gap-2 font-medium shrink-0" data-testid="header-search-submit">
        <Search className="w-4 h-4" />
        <span className="hidden sm:inline">{searchLabel}</span>
      </button>
      {showSuggestions && (hasSuggestions || (!query.trim() && trending?.length) || (query.trim().length >= 3)) && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-md shadow-lg z-[60] max-h-[420px] overflow-y-auto" data-testid="search-suggestions">
          {query.trim().length >= 3 && (
            <button
              type="button"
              onMouseDown={() => {
                const q = query.trim();
                const params = new URLSearchParams({ q });
                if (city && city !== 'All Telangana') params.set('city', city);
                navigate(`/ai-search?${params.toString()}`);
                setShowSuggestions(false);
              }}
              className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-orange-50 border-b border-gray-100 text-left transition-colors"
              data-testid="header-ask-ai"
            >
              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#ee5e23] to-[#d24e15] text-white flex items-center justify-center shrink-0">
                <Sparkles className="w-4 h-4" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-gray-900">Ask AI: &ldquo;{query}&rdquo;</div>
                <div className="text-[11px] text-gray-500">Understand intent + city + quantity — powered by Gemini</div>
              </div>
            </button>
          )}
          {hasSuggestions ? (
            <div>
              {suggestions.products?.length > 0 && (
                <div className="p-2 border-b border-gray-100">
                  <div className="text-[11px] font-semibold text-gray-500 uppercase px-2 py-1">Products</div>
                  {suggestions.products.map((p) => (
                    <button
                      key={p.id}
                      type="button"
                      onMouseDown={() => goToProduct(p)}
                      className="w-full flex items-center gap-3 px-2 py-2 hover:bg-orange-50 rounded text-left transition-colors"
                      data-testid={`suggest-product-${p.id}`}
                    >
                      <img src={p.image} alt="" className="w-9 h-9 rounded object-cover border" />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm text-gray-900 truncate">{p.name}</div>
                        <div className="text-xs text-[#2c54b2] font-medium">{inr(p.price)} <span className="text-gray-400">/ {p.unit}</span></div>
                      </div>
                      <Package className="w-4 h-4 text-gray-300" />
                    </button>
                  ))}
                </div>
              )}
              {suggestions.vendors?.length > 0 && (
                <div className="p-2 border-b border-gray-100">
                  <div className="text-[11px] font-semibold text-gray-500 uppercase px-2 py-1">Vendors</div>
                  {suggestions.vendors.map((v) => (
                    <button
                      key={v.id}
                      type="button"
                      onMouseDown={() => goToVendor(v)}
                      className="w-full flex items-center gap-3 px-2 py-2 hover:bg-orange-50 rounded text-left transition-colors"
                      data-testid={`suggest-vendor-${v.id}`}
                    >
                      {v.logo ? (
                        <img src={v.logo} alt="" className="w-9 h-9 rounded object-cover border" />
                      ) : (
                        <div className="w-9 h-9 rounded border bg-gray-50 flex items-center justify-center">
                          <Store className="w-4 h-4 text-[#2c54b2]" />
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="text-sm text-gray-900 truncate flex items-center gap-1.5">
                          {v.name}
                          {v.claimed && (
                            <span className="text-[9px] uppercase font-semibold tracking-wider bg-emerald-50 text-emerald-700 border border-emerald-200 rounded px-1 py-0.5">Verified</span>
                          )}
                        </div>
                        {v.city && <div className="text-xs text-gray-500 truncate">{v.city}</div>}
                      </div>
                      <Store className="w-4 h-4 text-gray-300" />
                    </button>
                  ))}
                </div>
              )}
              {suggestions.services?.length > 0 && (
                <div className="p-2 border-b border-gray-100">
                  <div className="text-[11px] font-semibold text-gray-500 uppercase px-2 py-1">Services</div>
                  {suggestions.services.map((s) => (
                    <button
                      key={s.id}
                      type="button"
                      onMouseDown={() => goToService(s)}
                      className="w-full flex items-center gap-3 px-2 py-2 hover:bg-orange-50 rounded text-left transition-colors"
                      data-testid={`suggest-service-${s.id}`}
                    >
                      {s.images?.[0] ? (
                        <img src={s.images[0]} alt="" className="w-9 h-9 rounded object-cover border" />
                      ) : (
                        <div className="w-9 h-9 rounded border bg-gray-50 flex items-center justify-center">
                          <Briefcase className="w-4 h-4 text-[#2c54b2]" />
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="text-sm text-gray-900 truncate">{s.name}</div>
                        {s.price_from != null && (
                          <div className="text-xs text-[#2c54b2] font-medium">From {inr(s.price_from)} <span className="text-gray-400">/ {s.price_unit || 'service'}</span></div>
                        )}
                      </div>
                      <Briefcase className="w-4 h-4 text-gray-300" />
                    </button>
                  ))}
                </div>
              )}
              {suggestions.sub_categories?.length > 0 && (
                <div className="p-2 border-b border-gray-100">
                  <div className="text-[11px] font-semibold text-gray-500 uppercase px-2 py-1">Sub-categories</div>
                  {suggestions.sub_categories.map((s) => (
                    <button
                      key={s.id}
                      type="button"
                      onMouseDown={() => goToSubCategory(s)}
                      className="w-full flex items-center gap-2 px-2 py-1.5 hover:bg-blue-50 rounded text-left transition-colors"
                      data-testid={`suggest-subcat-${s.id}`}
                    >
                      <Folder className="w-4 h-4 text-[#2c54b2]" />
                      <span className="text-sm text-gray-800">{s.name}</span>
                    </button>
                  ))}
                </div>
              )}
              {suggestions.categories?.length > 0 && (
                <div className="p-2">
                  <div className="text-[11px] font-semibold text-gray-500 uppercase px-2 py-1">Categories</div>
                  {suggestions.categories.map((c) => (
                    <button
                      key={c.id}
                      type="button"
                      onMouseDown={() => goToCategory(c)}
                      className="w-full flex items-center gap-2 px-2 py-1.5 hover:bg-blue-50 rounded text-left transition-colors"
                      data-testid={`suggest-cat-${c.id}`}
                    >
                      <Folder className="w-4 h-4 text-[#2c54b2]" />
                      <span className="text-sm text-gray-800">{c.name}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <div className="p-3">
              <div className="text-xs font-semibold text-gray-500 uppercase mb-2">Trending</div>
              <div className="flex flex-wrap gap-2">
                {trending.map((t) => (
                  <button
                    key={t}
                    type="button"
                    onMouseDown={() => { setQuery(t); navigate(`/search?q=${encodeURIComponent(t)}&city=${encodeURIComponent(city)}`); setShowSuggestions(false); }}
                    className="px-3 py-1 text-xs bg-gray-100 hover:bg-[#ee5e23] hover:text-white rounded-full transition-colors"
                  >{t}</button>
                ))}
              </div>
            </div>
          )}
          {suggestLoading && (
            <div className="px-3 py-2 text-xs text-gray-400" data-testid="suggest-loading">Searching…</div>
          )}
        </div>
      )}
    </form>
  );
};

export default HeaderSearch;
